import { useState } from "react";
import { Loader2, Save, Eye, EyeOff, CheckCircle2, KeyRound, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import OrientationPicker from "./OrientationPicker";

export default function ProfilePanel() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const change = async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            toast.error("New password must be at least 8 characters.");
            return;
        }
        if (next !== confirm) {
            toast.error("New passwords don't match.");
            return;
        }
        if (next === current) {
            toast.error("New password must differ from the current password.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password changed successfully.");
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Change failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="admin-profile" className="max-w-2xl">
            <h2 className="font-heading text-2xl font-bold mb-1">Profile & Security</h2>
            <p className="text-sm text-slate-500 mb-6">Manage your admin account.</p>

            {/* Account info */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-3 font-bold">Account</div>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                        <div className="text-xs text-slate-500">Name</div>
                        <div className="font-semibold text-[#0F172A]">{user?.name || "Admin"}</div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Email</div>
                        <div className="font-semibold text-[#0F172A] break-all">{user?.email || "—"}</div>
                    </div>
                    <div>
                        <div className="text-xs text-slate-500">Role</div>
                        <div className="font-semibold inline-flex items-center gap-1 text-[#1E3A8A]">
                            <CheckCircle2 className="w-4 h-4" /> {user?.role || "admin"}
                        </div>
                    </div>
                </div>
            </div>

            {/* V49 — Quick orientation toggle (mirrors Settings → Display Settings) */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="profile-orientation-quick">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-slate-500 mb-3 font-bold">
                    <Smartphone className="w-4 h-4" /> Display Orientation
                </div>
                <p className="text-xs text-slate-500 mb-4">
                    Quick toggle — change how the admin dashboard responds when you rotate your phone or tablet. Saved on this device only.
                </p>
                <OrientationPicker compact />
            </div>

            {/* Change password */}
            <form onSubmit={change} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4" data-testid="change-pwd-form">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">
                    <KeyRound className="w-4 h-4" /> Change Password
                </div>

                <Field label="Current Password">
                    <div className="relative">
                        <input
                            type={showPwd ? "text" : "password"}
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            required
                            data-testid="change-pwd-current"
                            className="w-full pr-10 px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                        <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700">
                            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </Field>

                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="New Password">
                        <input
                            type={showPwd ? "text" : "password"}
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            required minLength={8}
                            data-testid="change-pwd-new"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                    </Field>
                    <Field label="Confirm New Password">
                        <input
                            type={showPwd ? "text" : "password"}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            required minLength={8}
                            data-testid="change-pwd-confirm"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                        />
                    </Field>
                </div>

                {confirm && next !== confirm && (
                    <p className="text-xs text-red-500">New passwords don't match.</p>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="change-pwd-submit"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1E3A8A] text-white font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60"
                >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {submitting ? "Saving…" : "Update Password"}
                </button>
                <p className="text-xs text-slate-400">
                    Tip: Use a unique password of at least 12 characters with letters, numbers and symbols.
                </p>
            </form>
        </div>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</span>
        {children}
    </label>
);
